import { useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import { css } from '@emotion/css';

import { PrivacyFilter } from '#components/PrivacyFilter';
import { useFormat } from '#hooks/useFormat';

import { separarEmoji } from './emoji';
import { Hoja } from './Hoja';
import { Icono } from './Icono';
import { conSimboloEuro } from './Importe';
import { filtrarSecciones, primeraFila } from './seleccion';
import type { FilaSeleccion, SeccionSeleccion, Tramo } from './seleccion';
import {
  color,
  espacio,
  movimiento,
  num,
  radio,
  suave,
  TACTIL,
  texto,
} from './tokens';

export type { FilaSeleccion, SeccionSeleccion } from './seleccion';

/** Alto de cada fila elegible (por encima de los 48 px de zona táctil). */
export const ALTO_FILA_SELECCION = 56;

/** Saldo positivo legible en claro y en oscuro (verde acercado a la tinta). */
const TINTA_SALDO = `color-mix(in oklab, ${color.ok} 72%, ${color.fg})`;

/** Fila fija al final de la lista (p. ej. «Sin categoría»). */
export type FilaFinalSeleccion = {
  nombre: string;
  detalle?: string;
  /** Contenido de la cajita (por defecto «∅»). */
  icono?: ReactNode;
  /** Pintada como acción (en el color de acento). */
  accion?: boolean;
  onElegir: () => void;
};

type HojaSeleccionProps = {
  titulo: string;
  /** Línea bajo el título (beneficiario · importe · fecha). */
  contexto?: ReactNode;
  /** Texto del buscador vacío y su aria-label («Buscar categoría»). */
  textoBuscar: string;
  secciones: SeccionSeleccion[];
  onElegir: (fila: FilaSeleccion) => void;
  onCerrar: () => void;
  /** Pastilla a la derecha de cada fila (el saldo). */
  pastilla?: (fila: FilaSeleccion) => ReactNode;
  filaFinal?: FilaFinalSeleccion;
  /** Acción «Crear … «texto»» cuando la búsqueda no encuentra nada. */
  crear?: {
    etiqueta: (texto: string) => string;
    onCrear: (texto: string) => void;
  };
  /** Con el teclado abierto al montar (no, si la enseña la ayuda). */
  enfocar?: boolean;
  'data-testid'?: string;
};

/**
 * Hoja de selección con buscador (H76): sube al 90 % con el buscador ya
 * enfocado, atajos arriba (Sugerida, Recientes), luego las secciones con
 * cabecera pequeña en mayúsculas y filas de 56 px. Al buscar, una sola
 * lista de resultados con lo que coincide resaltado; Intro elige el primero.
 */
export function HojaSeleccion({
  titulo,
  contexto,
  textoBuscar,
  secciones,
  onElegir,
  onCerrar,
  pastilla,
  filaFinal,
  crear,
  enfocar = true,
  'data-testid': testId,
}: HojaSeleccionProps) {
  const { t } = useTranslation();
  const [consulta, setConsulta] = useState('');
  const entrada = useRef<HTMLInputElement>(null);
  const buscando = consulta.trim().length > 0;
  const resultados = useMemo(
    () => filtrarSecciones(secciones, consulta),
    [secciones, consulta],
  );

  // La hoja se abre para buscar: el teclado sale solo.
  useEffect(() => {
    if (enfocar) {
      entrada.current?.focus();
    }
    // Solo al montar.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const elegirPrimera = () => {
    if (buscando) {
      if (resultados.length > 0) {
        onElegir(resultados[0].fila);
      } else if (crear) {
        crear.onCrear(consulta.trim());
      }
      return;
    }
    const primera = primeraFila(secciones);
    if (primera) {
      onElegir(primera);
    }
  };

  return (
    <Hoja
      aria-label={titulo}
      data-testid={testId}
      onCerrar={onCerrar}
      alto="90%"
    >
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 10,
          paddingBottom: 10,
          flexShrink: 0,
        }}
      >
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text style={{ ...texto.titulo, color: color.fg }}>{titulo}</Text>
          {contexto && (
            <Text
              style={{
                fontSize: 12.5,
                fontWeight: 600,
                color: color.fg2,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {contexto}
            </Text>
          )}
        </View>
        <button
          type="button"
          aria-label={t('Close')}
          onClick={onCerrar}
          className={css({
            width: 36,
            height: 36,
            borderRadius: 18,
            border: 0,
            padding: 0,
            backgroundColor: color.surface2,
            color: color.fg,
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
            cursor: 'pointer',
            position: 'relative',
            // Zona táctil de 44 px sin agrandar el círculo.
            '&::after': {
              content: '""',
              position: 'absolute',
              inset: -(TACTIL - 36) / 2,
            },
          })}
        >
          <Icono nombre="x" size={18} />
        </button>
      </View>

      <label
        className={css({
          height: 48,
          borderRadius: radio.boton,
          backgroundColor: color.surface2,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '0 12px 0 14px',
          border: '2px solid transparent',
          flexShrink: 0,
          marginBottom: 6,
          '&:focus-within': { borderColor: color.accent },
        })}
      >
        <Icono nombre="search" size={18} style={{ color: color.fg2 }} />
        <input
          ref={entrada}
          type="text"
          inputMode="search"
          enterKeyHint="go"
          autoComplete="off"
          aria-label={textoBuscar}
          placeholder={textoBuscar}
          value={consulta}
          onChange={e => setConsulta(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              elegirPrimera();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              onCerrar();
            }
          }}
          className={css({
            flex: 1,
            minWidth: 0,
            border: 0,
            outline: 'none',
            background: 'transparent',
            color: color.fg,
            fontFamily: 'inherit',
            fontSize: 15.5,
            fontWeight: 700,
            '&::placeholder': { color: color.fg3, fontWeight: 600 },
          })}
        />
        {consulta && (
          <button
            type="button"
            aria-label={t('Clear search')}
            onClick={() => {
              setConsulta('');
              entrada.current?.focus();
            }}
            className={css({
              width: 26,
              height: 26,
              borderRadius: 13,
              border: 0,
              padding: 0,
              backgroundColor: color.line2,
              color: color.fg2,
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
              cursor: 'pointer',
              position: 'relative',
              // Zona táctil de 44 px (B2 · H04) sin agrandar el círculo: cabe
              // en la franja de 48 px; a la derecha hay 12 px de relleno.
              '&::after': {
                content: '""',
                position: 'absolute',
                inset: -(TACTIL - 26) / 2,
              },
            })}
          >
            <Icono nombre="x" size={14} />
          </button>
        )}
      </label>

      <View
        data-testid="hoja-seleccion-lista"
        // Al desplazar se esconde el teclado y se ve la lista entera.
        onTouchMove={() => entrada.current?.blur()}
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          margin: `0 -${espacio.fila}px`,
          paddingBottom: 'calc(16px + env(safe-area-inset-bottom))',
        }}
      >
        {/*
          Bloque que no encoge: dentro de la lista con scroll (columna flex
          de alto fijo) las secciones encogían y sus filas se solapaban.
        */}
        <View style={{ flexShrink: 0 }}>
          {buscando ? (
            <>
              {resultados.length > 0 && (
                <CabeceraSeccion
                  titulo={t('{{count}} results', { count: resultados.length })}
                />
              )}
              {resultados.length === 0 && (
                <Text
                  style={{
                    padding: `6px ${espacio.fila}px 0`,
                    fontSize: 12.5,
                    fontWeight: 600,
                    color: color.fg2,
                  }}
                >
                  <Trans>No results</Trans>
                </Text>
              )}
              {resultados.map(({ fila, tramo }, i) => (
                <FilaElegible
                  key={fila.id}
                  fila={{
                    ...fila,
                    destacada: false,
                    detalle: fila.detalleBusqueda ?? fila.detalle,
                  }}
                  tramo={tramo}
                  separador={i > 0}
                  pastilla={pastilla}
                  onElegir={onElegir}
                />
              ))}
              {resultados.length === 0 && crear && (
                <button
                  type="button"
                  onClick={() => crear.onCrear(consulta.trim())}
                  className={css({
                    ...estiloFila,
                    height: 52,
                    marginTop: 4,
                    borderTop: `1px solid ${color.line}`,
                    borderRadius: 0,
                    color: color.accent,
                    fontWeight: 800,
                    fontSize: 15,
                  })}
                >
                  <Cajita fondo={suave(color.accent, 14)}>
                    <Icono nombre="plus" size={18} />
                  </Cajita>
                  <span>{crear.etiqueta(consulta.trim())}</span>
                </button>
              )}
            </>
          ) : (
            <>
              {secciones.map(seccion =>
                seccion.filas.length === 0 ? null : (
                  <View
                    key={seccion.id}
                    role="group"
                    aria-label={seccion.titulo}
                  >
                    {seccion.sinCabecera ? (
                      <View style={{ height: 8 }} />
                    ) : (
                      <CabeceraSeccion
                        titulo={seccion.titulo}
                        icono={seccion.icono}
                        contador={seccion.contador}
                      />
                    )}
                    {seccion.filas.map((fila, i) => (
                      <FilaElegible
                        key={fila.id}
                        fila={fila}
                        separador={i > 0 && !seccion.filas[i - 1].destacada}
                        pastilla={pastilla}
                        onElegir={onElegir}
                      />
                    ))}
                  </View>
                ),
              )}
              {filaFinal && (
                <button
                  type="button"
                  onClick={filaFinal.onElegir}
                  className={css({
                    ...estiloFila,
                    marginTop: 6,
                    boxShadow: `inset 0 1px 0 ${color.line}`,
                    borderRadius: 0,
                  })}
                >
                  <Cajita
                    fondo="transparent"
                    style={{
                      border: `1.5px dashed ${color.line2}`,
                      color: filaFinal.accion ? color.accent : color.fg3,
                      fontSize: 16,
                      fontWeight: 800,
                    }}
                  >
                    {filaFinal.icono ?? '∅'}
                  </Cajita>
                  <TextoFila
                    nombre={filaFinal.nombre}
                    detalle={filaFinal.detalle}
                    tinta={filaFinal.accion ? color.accent : color.fg2}
                  />
                </button>
              )}
            </>
          )}
        </View>
      </View>
    </Hoja>
  );
}

const estiloFila = {
  display: 'flex',
  alignItems: 'center',
  gap: espacio.icono,
  width: '100%',
  minHeight: ALTO_FILA_SELECCION,
  height: ALTO_FILA_SELECCION,
  padding: `0 ${espacio.fila}px`,
  border: 0,
  borderRadius: radio.boton,
  background: 'transparent',
  color: color.fg,
  textAlign: 'left',
  fontFamily: 'inherit',
  cursor: 'pointer',
  flexShrink: 0,
  transition: `transform ${movimiento.pulsar}ms ${movimiento.muelle}`,
  '&:active': { transform: 'scale(0.98)' },
} as const;

function CabeceraSeccion({
  titulo,
  icono,
  contador,
}: {
  titulo: string;
  icono?: SeccionSeleccion['icono'];
  contador?: number;
}) {
  return (
    <View
      style={{
        ...texto.etiqueta,
        letterSpacing: '0.08em',
        color: color.fg3,
        padding: `14px ${espacio.fila}px 4px`,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
      }}
    >
      {icono && (
        <Icono nombre={icono} size={14} style={{ color: color.accent }} />
      )}
      <Text>{titulo}</Text>
      {contador != null && (
        <Text
          style={{
            marginLeft: 'auto',
            letterSpacing: 0,
            textTransform: 'none',
            fontWeight: 700,
          }}
        >
          {contador}
        </Text>
      )}
    </View>
  );
}

function Cajita({
  fondo,
  style,
  children,
}: {
  fondo: string;
  style?: CSSProperties;
  children: ReactNode;
}) {
  return (
    <span
      aria-hidden
      style={{
        width: 36,
        height: 36,
        borderRadius: radio.sm,
        backgroundColor: fondo,
        display: 'grid',
        placeItems: 'center',
        fontSize: 19,
        flexShrink: 0,
        boxSizing: 'border-box',
        ...style,
      }}
    >
      {children}
    </span>
  );
}

function TextoFila({
  nombre,
  detalle,
  tramo,
  tinta = color.fg,
}: {
  nombre: string;
  detalle?: string;
  tramo?: Tramo | null;
  tinta?: string;
}) {
  return (
    <span
      style={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        ...texto.fila,
        color: tinta,
      }}
    >
      <span
        style={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {tramo ? (
          <>
            {nombre.slice(0, tramo.inicio)}
            <mark
              style={{
                backgroundColor: suave(color.accent, 24),
                color: 'inherit',
                borderRadius: 4,
                padding: '0 1px',
              }}
            >
              {nombre.slice(tramo.inicio, tramo.fin)}
            </mark>
            {nombre.slice(tramo.fin)}
          </>
        ) : (
          nombre
        )}
      </span>
      {detalle && (
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: color.fg3,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {detalle}
        </span>
      )}
    </span>
  );
}

function FilaElegible({
  fila,
  tramo,
  separador,
  pastilla,
  onElegir,
}: {
  fila: FilaSeleccion;
  tramo?: Tramo | null;
  separador: boolean;
  pastilla?: (fila: FilaSeleccion) => ReactNode;
  onElegir: (fila: FilaSeleccion) => void;
}) {
  return (
    <button
      type="button"
      data-destacada={fila.destacada || undefined}
      onClick={() => onElegir(fila)}
      className={css({
        ...estiloFila,
        ...(separador && { boxShadow: `inset 0 1px 0 ${color.line}` }),
        ...(fila.destacada && {
          backgroundColor: color.accentSoft,
          boxShadow: `inset 0 0 0 1.5px ${suave(color.accent, 45)}`,
        }),
      })}
    >
      <Cajita fondo={color.surface2}>
        {fila.emoji ?? (
          <span style={{ fontSize: 15, fontWeight: 800, color: color.fg2 }}>
            {fila.nombre.trim().charAt(0).toUpperCase()}
          </span>
        )}
      </Cajita>
      <TextoFila nombre={fila.nombre} detalle={fila.detalle} tramo={tramo} />
      {pastilla?.(fila)}
    </button>
  );
}

/**
 * Pastilla de saldo como la del Plan, siempre con €: verde con texto oscuro
 * legible si hay dinero, gris si es 0 y roja si está en negativo.
 */
export function PastillaSaldo({ valor }: { valor: number | null | undefined }) {
  const format = useFormat();
  if (valor == null) {
    return null;
  }
  const cifra = format(valor, 'financial');
  const textoCifra = conSimboloEuro(cifra);
  const [fondo, tinta, estado] =
    valor > 0
      ? [color.okSoft, TINTA_SALDO, 'ok']
      : valor < 0
        ? [color.badSoft, color.bad, 'rojo']
        : [color.muteSoft, color.fg2, 'neutro'];
  return (
    <PrivacyFilter>
      <span
        data-estado={estado}
        style={{
          ...num,
          borderRadius: radio.pildora,
          padding: '4px 10px',
          fontSize: 13.5,
          fontWeight: 800,
          whiteSpace: 'nowrap',
          flexShrink: 0,
          backgroundColor: fondo,
          color: tinta,
        }}
      >
        {textoCifra}
      </span>
    </PrivacyFilter>
  );
}

/** Fila de selección a partir de un nombre que puede llevar emoji delante. */
export function filaConEmoji(
  id: string,
  nombreCompleto: string,
  extra: Omit<FilaSeleccion, 'id' | 'nombre' | 'emoji'> = {},
): FilaSeleccion {
  const { emoji, resto } = separarEmoji(nombreCompleto);
  return { id, nombre: emoji ? resto : nombreCompleto, emoji, ...extra };
}
